import { NODE_ENV } from '@/config/env'

/**
 * Environment check for development mode
 * Used to conditionally enable logging based on the current environment
 */
const isDev = NODE_ENV === 'development'

/**
 * Log levels for different types of messages
 */
export enum LogLevel {
  LOG = 'log',
  WARN = 'warn',
  ERROR = 'error',
  INFO = 'info',
  DEBUG = 'debug',
}

/**
 * Logger utility for consistent logging across the application
 *
 * This logger provides a centralized way to handle console output with environment-aware behavior:
 * - In development: All log levels are enabled
 * - In production: Only error logs are displayed to avoid console pollution
 *
 * @example
 * ```typescript
 * import logger from '@/utils/logger'
 *
 * // Development: logs to console
 * // Production: no output
 * logger.log('User logged in:', userData)
 *
 * // Development: logs to console
 * // Production: no output
 * logger.warn('Deprecated API called')
 *
 * // Always logs regardless of environment
 * logger.error('Failed to fetch data:', error)
 *
 * // New features
 * logger.info('Application started')
 * logger.debug('Component state:', state)
 * logger.group('API Calls', () => {
 *   logger.log('GET /users')
 *   logger.log('POST /auth')
 * })
 * ```
 */
const logger = {
  /**
   * Logs informational messages
   * Only outputs in development environment
   *
   * @param {...any} args - Arguments to log (same as console.log)
   * @example
   * logger.log('Component mounted')
   * logger.log('API response:', response)
   */
  log: (...args: any[]) => {
    if (isDev) {
      console.log(`[${new Date().toISOString()}]`, ...args)
    }
  },

  /**
   * Logs warning messages
   * Only outputs in development environment
   *
   * @param {...any} args - Arguments to log (same as console.warn)
   * @example
   * logger.warn('Deprecated method called')
   * logger.warn('Missing required prop:', propName)
   */
  warn: (...args: any[]) => {
    if (isDev) {
      console.warn(`[${new Date().toISOString()}]`, ...args)
    }
  },

  /**
   * Logs error messages
   * Always outputs regardless of environment
   *
   * @param {...any} args - Arguments to log (same as console.error)
   * @example
   * logger.error('API request failed:', error)
   * logger.error('Invalid user input:', input)
   */
  error: (...args: any[]) => {
    console.error(`[${new Date().toISOString()}]`, ...args)
  },

  /**
   * Logs info messages (alias for log with different styling)
   * Only outputs in development environment
   *
   * @param {...any} args - Arguments to log
   * @example
   * logger.info('Application started')
   * logger.info('User session created')
   */
  info: (...args: any[]) => {
    if (isDev) {
      console.info(`[${new Date().toISOString()}] ℹ️`, ...args)
    }
  },

  /**
   * Logs debug messages with special formatting
   * Only outputs in development environment
   *
   * @param {...any} args - Arguments to log
   * @example
   * logger.debug('Component state:', state)
   * logger.debug('API request params:', params)
   */
  debug: (...args: any[]) => {
    if (isDev) {
      console.debug(`[${new Date().toISOString()}] 🐛`, ...args)
    }
  },

  /**
   * Groups related log messages together
   * Only outputs in development environment
   *
   * @param {string} label - The group label
   * @param {Function} fn - Function containing the grouped logs
   * @example
   * logger.group('API Calls', () => {
   *   logger.log('GET /users')
   *   logger.log('POST /auth')
   * })
   */
  group: (label: string, fn: () => void) => {
    if (isDev) {
      console.group(`[${new Date().toISOString()}] ${label}`)
      try {
        fn()
      } finally {
        console.groupEnd()
      }
    }
  },

  /**
   * Creates a collapsed group of related log messages
   * Only outputs in development environment
   *
   * @param {string} label - The group label
   * @param {Function} fn - Function containing the grouped logs
   * @example
   * logger.groupCollapsed('User Actions', () => {
   *   logger.log('Button clicked')
   *   logger.log('Form submitted')
   * })
   */
  groupCollapsed: (label: string, fn: () => void) => {
    if (isDev) {
      console.groupCollapsed(`[${new Date().toISOString()}] ${label}`)
      try {
        fn()
      } finally {
        console.groupEnd()
      }
    }
  },

  /**
   * Logs a table of data
   * Only outputs in development environment
   *
   * @param {any} data - Data to display as a table
   * @param {string[]} columns - Optional columns to display
   * @example
   * logger.table(users, ['id', 'name', 'email'])
   */
  table: (data: any, columns?: string[]) => {
    if (isDev) {
      console.table(data, columns)
    }
  },

  /**
   * Logs the current call stack trace
   * Only outputs in development environment
   *
   * @param {string} label - Optional label for the trace
   * @example
   * logger.trace('Function entry point')
   */
  trace: (label?: string) => {
    if (isDev) {
      if (label) {
        console.trace(`[${new Date().toISOString()}] ${label}`)
      } else {
        console.trace(`[${new Date().toISOString()}]`)
      }
    }
  },

  /**
   * Measures the time taken by a function
   * Only outputs in development environment
   *
   * @param {string} label - Label for the measurement
   * @param {Function} fn - Function to measure
   * @returns {any} The result of the function
   * @example
   * const result = await logger.time('API Call', async () => {
   *   return await fetch('/api/data')
   * })
   */
  time: async <T>(label: string, fn: () => T | Promise<T>): Promise<T> => {
    if (isDev) {
      console.time(`[${new Date().toISOString()}] ${label}`)
      try {
        const result = await fn()
        return result
      } finally {
        console.timeEnd(`[${new Date().toISOString()}] ${label}`)
      }
    } else {
      return await fn()
    }
  },

  /**
   * Clears the console
   * Only works in development environment
   *
   * @example
   * logger.clear()
   */
  clear: () => {
    if (isDev) {
      console.clear()
    }
  },
}

export default logger
